import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import cx from "classnames";
import { useTheme } from "styled-components";
import { useDropzone } from "react-dropzone";
import { Icons } from "components";
import { UploadFileNewStyled, ShowImgBox } from "./styled";

const FILE_SIZE_ERROR_TH =
  "ไฟล์มีขนาดใหญ่เกินไป กรุณาอัปโหลดรูปภาพอีกครั้ง";

const resolveAccept = (acceptType) => {
  switch (acceptType) {
    case "cover":
      return ".png,.jpg,.jpeg";
    case "audio":
      return ".mp3,audio/mpeg3";
    case "video":
      return "video/*";
    default:
      return "image/*";
  }
};

const getPreviewUrl = (file) => {
  if (!file || file.isDelete) return "";
  if (file.preview) return file.preview;
  if (file.image_uri) return file.image_uri;
  if (file.media_uri) return file.media_uri;
  return "";
};

const UploadFileNew = ({
  theme_upload_set_image,
  useOnchangeRemoveEvent = true,
  heightBox,
  acceptType = "cover",
  labelTop,
  labelBottom,
  iconWidth = "32",
  iconHeight = "32",
  value,
  maxLength,
  disabled,
  desc_text,
  onChange,
  fontSizeLabelTop,
  fontSizeLabelBottom,
  fontFamilyLabelTop,
  fontFamilyLabelBottom,
  maxMBFileSizeWarning,
  multiple = true,
  name,
  width = "100%",
  dataTestId,
  theme_left_right = false,
}) => {
  const theme = useTheme();
  const [files, setFiles] = useState(() =>
    Array.isArray(value) ? value : []
  );
  const [error, setError] = useState("");
  const filesRef = useRef(files);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const iconW = Number(iconWidth) || 32;
  const iconH = Number(iconHeight) || 32;

  const effectiveMax = useMemo(() => {
    if (maxLength != null) return maxLength;
    if (!multiple) return 1;
    return null;
  }, [maxLength, multiple]);

  const visibleFiles = useMemo(
    () => (Array.isArray(files) ? files.filter((f) => f && !f.isDelete) : []),
    [files]
  );

  const atCapacity = useMemo(() => {
    if (effectiveMax == null) return false;
    return visibleFiles.length >= effectiveMax;
  }, [effectiveMax, visibleFiles.length]);

  useEffect(() => {
    if (Array.isArray(value)) {
      setFiles(value);
    }
  }, [value]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (disabled || !acceptedFiles?.length) return;

      setError("");

      if (maxMBFileSizeWarning != null && maxMBFileSizeWarning !== "") {
        const maxMb = Number(maxMBFileSizeWarning);
        if (Number.isFinite(maxMb)) {
          const oversized = acceptedFiles.filter(
            (f) => f.size > maxMb * 1024 * 1024
          );
          if (oversized.length > 0) {
            setError(FILE_SIZE_ERROR_TH);
            return;
          }
        }
      }

      const list = Array.isArray(filesRef.current) ? filesRef.current : [];
      const active = list.filter((f) => f && !f.isDelete);
      const deleted = list.filter((f) => f?.isDelete);

      let toProcess = [...acceptedFiles];
      if (!multiple) {
        toProcess = toProcess.slice(0, 1);
      } else if (effectiveMax != null) {
        const slots = effectiveMax - active.length;
        if (slots <= 0) return;
        toProcess = toProcess.slice(0, slots);
      }

      if (!toProcess.length) return;

      const stamped = toProcess.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
          isNewUpload: true,
          fieldName: name,
        })
      );

      let next;
      if (!multiple) {
        active.forEach((f) => {
          if (f?.preview && f.isNewUpload) {
            URL.revokeObjectURL(f.preview);
          }
        });
        next = [...deleted, ...stamped];
      } else {
        const merged = [...active, ...stamped];
        const capped =
          effectiveMax != null ? merged.slice(0, effectiveMax) : merged;
        next = [...deleted, ...capped];
      }

      filesRef.current = next;
      setFiles(next);
      if (onChange) onChange(next);
    },
    [
      disabled,
      maxMBFileSizeWarning,
      multiple,
      effectiveMax,
      onChange,
      name,
    ]
  );

  const maxDropFiles = useMemo(() => {
    if (!multiple) return 1;
    if (effectiveMax == null) return undefined;
    const n = effectiveMax - visibleFiles.length;
    return n > 0 ? n : 0;
  }, [multiple, effectiveMax, visibleFiles.length]);

  const dropzoneOpts = useMemo(() => {
    const opts = {
      accept: resolveAccept(acceptType),
      onDrop,
      multiple,
      disabled: Boolean(disabled || atCapacity),
    };
    if (maxDropFiles != null && maxDropFiles > 0) {
      opts.maxFiles = maxDropFiles;
    }
    return opts;
  }, [acceptType, onDrop, multiple, disabled, atCapacity, maxDropFiles]);

  const { getRootProps, getInputProps } = useDropzone(dropzoneOpts);

  const handleRemove = useCallback(
    (index, file) => {
      setError("");
      setFiles((prev) => {
        const next = [...prev];
        if (file.isNewUpload) {
          if (file.preview) URL.revokeObjectURL(file.preview);
          next.splice(index, 1);
        } else if (next[index]) {
          next[index] = { ...next[index], isDelete: true };
        }
        filesRef.current = next;
        if (useOnchangeRemoveEvent && onChange) {
          onChange(next);
        }
        return next;
      });
    },
    [onChange, useOnchangeRemoveEvent]
  );

  const rootClass = cx("upload_file_new_inner", {
    theme_upload_set_image: theme_upload_set_image,
    theme_left_right: theme_left_right,
  });

  const hasInternalError = Boolean(error);
  const firstVisible = visibleFiles[0];
  const showPreview = Boolean(firstVisible && getPreviewUrl(firstVisible));

  const showEmptyDropzone = !showPreview && !atCapacity;
  const showAddDropzone = showPreview && multiple && !atCapacity;

  return (
    <UploadFileNewStyled
      $width={width}
      $heightBox={heightBox}
      $iconW={iconW}
      $iconH={iconH}
      $fontTopSize={fontSizeLabelTop}
      $fontBotSize={fontSizeLabelBottom}
      $fontTopFam={fontFamilyLabelTop}
      $fontBotFam={fontFamilyLabelBottom}
      data-testid={dataTestId}
    >
      <div className={rootClass}>
        {showPreview
          ? visibleFiles.map((file, i) => {
            const url = getPreviewUrl(file);
            if (!url) return null;
            const realIndex = files.indexOf(file);
            return (
              <ShowImgBox
                key={`${url}-${String(i)}`}
                className={cx({ has_error: hasInternalError })}
                $src={url}
                $width={width}
                $heightBox={heightBox}
              >
                {!disabled && (
                  <button
                    type="button"
                    className="remove_btn"
                    disabled={disabled}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemove(realIndex, file);
                    }}
                    aria-label="ลบรูปภาพ"
                  >
                    <Icons.Cross
                      width="32"
                      height="32"
                      color={theme.COLORS.RED_3}
                      bg={theme.COLORS.WHITE_1}
                    />
                  </button>
                )}
              </ShowImgBox>
            );
          })
          : null}

        {showEmptyDropzone ? (
          <div
            {...getRootProps()}
            className={cx("box_upload", {
              has_error: hasInternalError,
              disabled: Boolean(disabled),
            })}
          >
            <input {...getInputProps({ name })} />
            <div className="upload_icon_wrap">
              <Icons.UploadImage />
            </div>
            {labelTop ? <div className="label_top">{labelTop}</div> : null}
            {labelBottom ? (
              <div className="label_bottom">{labelBottom}</div>
            ) : null}
          </div>
        ) : null}

        {showAddDropzone ? (
          <div
            {...getRootProps()}
            className={cx("box_upload", {
              has_error: hasInternalError,
              disabled: Boolean(disabled),
            })}
          >
            <input {...getInputProps({ name })} />
            <div className="upload_icon_wrap">
              <Icons.Upload color={theme.COLORS.GRAY_24} />
            </div>
            {labelTop ? <div className="label_top">{labelTop}</div> : null}
            {labelBottom ? (
              <div className="label_bottom">{labelBottom}</div>
            ) : null}
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="internal_error" role="alert">
          {error}
        </div>
      ) : null}
      {desc_text ? <div className="desc_text">{desc_text}</div> : null}
    </UploadFileNewStyled>
  );
};

export default UploadFileNew;
